import { FormData, ValidationError } from '../types';

export const validationRules = {
  age: (value: string | number): string | null => {
    const age = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(age)) return 'Please enter a valid age';
    if (age < 11) return 'Age must be at least 11';
    if (age > 99) return 'Age must be less than 100';
    if (age < 18) return 'RED_FLAG_MINOR';
    if (age > 80) return 'RED_FLAG_ELDERLY';
    return null;
  },

  height_cm: (value: string | number): string | null => {
    const height = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(height) || height < 140 || height > 220) {
      return 'Height must be between 140-220 cm';
    }
    return null;
  },

  height_feet: (value: string | number): string | null => {
    const feet = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(feet) || feet < 4 || feet > 7) {
      return 'Feet must be between 4-7';
    }
    return null;
  },

  height_inches: (value: string | number): string | null => {
    const inches = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(inches) || inches < 0 || inches > 11) {
      return 'Inches must be between 0-11';
    }
    return null;
  },

  weight_kg: (value: string | number): string | null => {
    const weight = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(weight) || weight < 40 || weight > 200) {
      return 'Weight must be between 40-200 kg';
    }
    return null;
  },

  tester_name: (value: string): string | null => {
    if (!value || value.trim() === '') {
      return 'Tester name is not filled';
    }
    if (value.trim().length < 2) {
      return 'Tester name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Tester name must be less than 100 characters';
    }
    return null;
  },
};

export function validateField(
  fieldName: keyof FormData,
  value: any,
  isRequired: boolean = false
): string | null {
  // Check required
  if (isRequired && (!value || (typeof value === 'string' && value.trim() === ''))) {
    return 'This field is required';
  }

  // Run specific validation rule if exists
  if (validationRules[fieldName as keyof typeof validationRules]) {
    return validationRules[fieldName as keyof typeof validationRules](value);
  }

  return null;
}

/**
 * Validate a radio group field
 */
export function validateRadioGroup(
  _fieldName: keyof FormData,
  value: string | undefined,
  isRequired: boolean = false
): string | null {
  if (isRequired && (!value || value === '')) {
    return 'Please select an option';
  }
  return null;
}

/**
 * Validate a checkbox group field
 */
export function validateCheckboxGroup(
  _fieldName: keyof FormData,
  value: string[] | undefined,
  isRequired: boolean = false
): string | null {
  if (isRequired && (!value || value.length === 0)) {
    return 'Please select at least one option';
  }
  return null;
}

/**
 * Validate a single section (only visible/conditional fields)
 */
export function validateSection(
  sectionNumber: number,
  formData: FormData
): ValidationError[] {
  const errors: ValidationError[] = [];

  switch (sectionNumber) {
    case 1:
      // Section 1: Client Information
      if (!formData.full_name?.trim()) {
        errors.push({ field: 'full_name', message: 'This field is required' });
      }
      const ageError = validateField('age', formData.age, true);
      if (ageError && !ageError.startsWith('RED_FLAG')) {
        errors.push({ field: 'age', message: ageError });
      }
      // Height validation (either cm or ft-in)
      if (formData.height_cm) {
        const heightError = validateField('height_cm', formData.height_cm, true);
        if (heightError) errors.push({ field: 'height_cm', message: heightError });
      } else if (formData.height_feet && formData.height_inches !== undefined) {
        const feetError = validateField('height_feet', formData.height_feet, true);
        if (feetError) errors.push({ field: 'height_feet', message: feetError });
        const inchesError = validateField('height_inches', formData.height_inches, true);
        if (inchesError) errors.push({ field: 'height_inches', message: inchesError });
      } else {
        errors.push({ field: 'height_cm', message: 'Height is required' });
      }
      const weightError = validateField('weight_kg', formData.weight_kg, true);
      if (weightError) errors.push({ field: 'weight_kg', message: weightError });
      if (!formData.city?.trim()) {
        errors.push({ field: 'city', message: 'This field is required' });
      }
      const occupationError = validateRadioGroup('occupation', formData.occupation, true);
      if (occupationError) errors.push({ field: 'occupation', message: occupationError });
      const relationshipError = validateRadioGroup('relationship_status', formData.relationship_status, true);
      if (relationshipError) errors.push({ field: 'relationship_status', message: relationshipError });
      const sexualActivityError = validateRadioGroup('current_sexual_activity', formData.current_sexual_activity, true);
      if (sexualActivityError) errors.push({ field: 'current_sexual_activity', message: sexualActivityError });
      const firstConsultationError = validateRadioGroup('first_consultation', formData.first_consultation, true);
      if (firstConsultationError) errors.push({ field: 'first_consultation', message: firstConsultationError });
      // Previous treatments (conditional)
      if (formData.first_consultation === 'no') {
        const prevTreatmentsError = validateCheckboxGroup('previous_treatments', formData.previous_treatments, true);
        if (prevTreatmentsError) errors.push({ field: 'previous_treatments', message: prevTreatmentsError });
      }
      const redFlagsError = validateRadioGroup('emergency_red_flags', formData.emergency_red_flags, true);
      if (redFlagsError) errors.push({ field: 'emergency_red_flags', message: redFlagsError });
      break;

    case 2:
      // Section 2: ED/PE Branch
      const mainIssueError = validateRadioGroup('main_issue', formData.main_issue, true);
      if (mainIssueError) errors.push({ field: 'main_issue', message: mainIssueError });
      
      // ED Branch (conditional)
      if (formData.main_issue === 'ed' || formData.main_issue === 'both') {
        const edDurationError = validateRadioGroup('ed_duration', formData.ed_duration, true);
        if (edDurationError) errors.push({ field: 'ed_duration', message: edDurationError });
        const edGetsErectionsError = validateRadioGroup('ed_gets_erections', formData.ed_gets_erections, true);
        if (edGetsErectionsError) errors.push({ field: 'ed_gets_erections', message: edGetsErectionsError });
        // ED follow-up (conditional)
        if (formData.ed_gets_erections === 'yes') {
          const edArousalError = validateRadioGroup('ed_partner_arousal_speed', formData.ed_partner_arousal_speed, true);
          if (edArousalError) errors.push({ field: 'ed_partner_arousal_speed', message: edArousalError });
          const edMaintenanceError = validateRadioGroup('ed_partner_maintenance', formData.ed_partner_maintenance, true);
          if (edMaintenanceError) errors.push({ field: 'ed_partner_maintenance', message: edMaintenanceError });
          const edHardnessError = validateRadioGroup('ed_partner_hardness', formData.ed_partner_hardness, true);
          if (edHardnessError) errors.push({ field: 'ed_partner_hardness', message: edHardnessError });
          const edMorningError = validateRadioGroup('ed_morning_erections', formData.ed_morning_erections, true);
          if (edMorningError) errors.push({ field: 'ed_morning_erections', message: edMorningError });
          const edMasturbationError = validateRadioGroup('ed_masturbation_imagination', formData.ed_masturbation_imagination, true);
          if (edMasturbationError) errors.push({ field: 'ed_masturbation_imagination', message: edMasturbationError });
        }
      }
      
      // PE Branch (conditional)
      if (formData.main_issue === 'pe' || formData.main_issue === 'both') {
        const peDurationError = validateRadioGroup('pe_duration', formData.pe_duration, true);
        if (peDurationError) errors.push({ field: 'pe_duration', message: peDurationError });
        const peEjaculationError = validateRadioGroup('pe_partner_ejaculation_time', formData.pe_partner_ejaculation_time, true);
        if (peEjaculationError) errors.push({ field: 'pe_partner_ejaculation_time', message: peEjaculationError });
        const peSensitivityError = validateRadioGroup('pe_partner_penile_sensitivity', formData.pe_partner_penile_sensitivity, true);
        if (peSensitivityError) errors.push({ field: 'pe_partner_penile_sensitivity', message: peSensitivityError });
        const peControlError = validateRadioGroup('pe_partner_masturbation_control', formData.pe_partner_masturbation_control, true);
        if (peControlError) errors.push({ field: 'pe_partner_masturbation_control', message: peControlError });
      }
      
      // Issues all partners (conditional - shown if multiple_partners is "yes" AND main_issue is NOT "others")
      if (formData.multiple_partners === 'yes' && formData.main_issue !== 'others') {
        const issuesAllError = validateRadioGroup('issues_all_partners', formData.issues_all_partners, true);
        if (issuesAllError) errors.push({ field: 'issues_all_partners', message: issuesAllError });
      }
      break;

    case 3:
      // Section 3: Masturbation & Behavioral History
      const masturbationMethodError = validateRadioGroup('masturbation_method', formData.masturbation_method, true);
      if (masturbationMethodError) errors.push({ field: 'masturbation_method', message: masturbationMethodError });
      // Grip (conditional)
      if (formData.masturbation_method === 'hands' || formData.masturbation_method === 'both') {
        const gripError = validateRadioGroup('masturbation_grip', formData.masturbation_grip, true);
        if (gripError) errors.push({ field: 'masturbation_grip', message: gripError });
      }
      // Frequency (conditional)
      if (formData.masturbation_method !== 'none') {
        const frequencyError = validateRadioGroup('masturbation_frequency', formData.masturbation_frequency, true);
        if (frequencyError) errors.push({ field: 'masturbation_frequency', message: frequencyError });
      }
      const pornError = validateRadioGroup('porn_frequency', formData.porn_frequency, true);
      if (pornError) errors.push({ field: 'porn_frequency', message: pornError });
      const anxietyError = validateRadioGroup('performance_anxiety', formData.performance_anxiety, true);
      if (anxietyError) errors.push({ field: 'performance_anxiety', message: anxietyError });
      // Partner response (conditional)
      if (formData.current_sexual_activity === 'yes') {
        const partnerResponseError = validateRadioGroup('partner_response', formData.partner_response, true);
        if (partnerResponseError) errors.push({ field: 'partner_response', message: partnerResponseError });
      }
      break;

    case 4:
      // Section 4: Medical & Lifestyle
      const medicalConditionsError = validateCheckboxGroup('medical_conditions', formData.medical_conditions, true);
      if (medicalConditionsError) errors.push({ field: 'medical_conditions', message: medicalConditionsError });
      if (formData.medical_conditions?.includes('other')) {
        if (!formData.medical_conditions_other?.trim()) {
          errors.push({ field: 'medical_conditions_other', message: 'Please specify the condition' });
        }
      }
      const medicationsError = validateCheckboxGroup('current_medications', formData.current_medications, true);
      if (medicationsError) errors.push({ field: 'current_medications', message: medicationsError });
      if (formData.current_medications?.includes('other')) {
        if (!formData.current_medications_other?.trim()) {
          errors.push({ field: 'current_medications_other', message: 'Please specify the medication' });
        }
      }
      const surgeryError = validateRadioGroup('spinal_genital_surgery', formData.spinal_genital_surgery, true);
      if (surgeryError) errors.push({ field: 'spinal_genital_surgery', message: surgeryError });
      const alcoholError = validateRadioGroup('alcohol_frequency', formData.alcohol_frequency, true);
      if (alcoholError) errors.push({ field: 'alcohol_frequency', message: alcoholError });
      const smokingError = validateRadioGroup('smoking_frequency', formData.smoking_frequency, true);
      if (smokingError) errors.push({ field: 'smoking_frequency', message: smokingError });
      const stressError = validateRadioGroup('stress_level', formData.stress_level, true);
      if (stressError) errors.push({ field: 'stress_level', message: stressError });
      const sleepError = validateRadioGroup('sleep_quality', formData.sleep_quality, true);
      if (sleepError) errors.push({ field: 'sleep_quality', message: sleepError });
      const activityError = validateRadioGroup('physical_activity', formData.physical_activity, true);
      if (activityError) errors.push({ field: 'physical_activity', message: activityError });
      break;

    case 5:
      // Section 5: Other Information (optional, but validate length if provided)
      if (formData.additional_info && formData.additional_info.length > 1000) {
        errors.push({ field: 'additional_info', message: 'Additional information must be less than 1000 characters' });
      }
      break;
  }

  return errors;
}

export function validateForm(formData: FormData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate tester name
  const testerNameError = validateField('tester_name', formData.tester_name, true);
  if (testerNameError) {
    errors.push({ field: 'tester_name', message: testerNameError });
  }

  // Validate Section 1 fields
  if (!formData.full_name?.trim()) {
    errors.push({ field: 'full_name', message: 'This field is required' });
  }
  
  const ageError = validateField('age', formData.age, true);
  if (ageError && !ageError.startsWith('RED_FLAG')) {
    errors.push({ field: 'age', message: ageError });
  }

  // Height validation (either cm or ft-in)
  if (formData.height_cm) {
    const heightError = validateField('height_cm', formData.height_cm, true);
    if (heightError) errors.push({ field: 'height_cm', message: heightError });
  } else if (formData.height_feet && formData.height_inches !== undefined) {
    const feetError = validateField('height_feet', formData.height_feet, true);
    if (feetError) errors.push({ field: 'height_feet', message: feetError });
    const inchesError = validateField('height_inches', formData.height_inches, true);
    if (inchesError) errors.push({ field: 'height_inches', message: inchesError });
  } else {
    errors.push({ field: 'height_cm', message: 'Height is required' });
  }

  const weightError = validateField('weight_kg', formData.weight_kg, true);
  if (weightError) errors.push({ field: 'weight_kg', message: weightError });

  if (!formData.city?.trim()) {
    errors.push({ field: 'city', message: 'This field is required' });
  }

  if (!formData.occupation) {
    errors.push({ field: 'occupation', message: 'This field is required' });
  }

  if (!formData.relationship_status) {
    errors.push({ field: 'relationship_status', message: 'This field is required' });
  }

  if (!formData.current_sexual_activity) {
    errors.push({ field: 'current_sexual_activity', message: 'This field is required' });
  }

  if (!formData.first_consultation) {
    errors.push({ field: 'first_consultation', message: 'This field is required' });
  }

  // Previous treatments (required if first_consultation is 'no')
  if (formData.first_consultation === 'no') {
    if (!formData.previous_treatments || formData.previous_treatments.length === 0) {
      errors.push({ field: 'previous_treatments', message: 'This field is required' });
    }
  }

  if (!formData.emergency_red_flags) {
    errors.push({ field: 'emergency_red_flags', message: 'This field is required' });
  }

  // Section 2: ED/PE Branch
  if (!formData.main_issue) {
    errors.push({ field: 'main_issue', message: 'This field is required' });
  }

  // ED Branch validations (if main_issue is 'ed' or 'both')
  if (formData.main_issue === 'ed' || formData.main_issue === 'both') {
    if (!formData.ed_duration) {
      errors.push({ field: 'ed_duration', message: 'This field is required' });
    }
    if (!formData.ed_gets_erections) {
      errors.push({ field: 'ed_gets_erections', message: 'This field is required' });
    }
    // ED follow-up questions (if ed_gets_erections is 'yes')
    if (formData.ed_gets_erections === 'yes') {
      if (!formData.ed_partner_arousal_speed) {
        errors.push({ field: 'ed_partner_arousal_speed', message: 'This field is required' });
      }
      if (!formData.ed_partner_maintenance) {
        errors.push({ field: 'ed_partner_maintenance', message: 'This field is required' });
      }
      if (!formData.ed_partner_hardness) {
        errors.push({ field: 'ed_partner_hardness', message: 'This field is required' });
      }
      if (!formData.ed_morning_erections) {
        errors.push({ field: 'ed_morning_erections', message: 'This field is required' });
      }
      if (!formData.ed_masturbation_imagination) {
        errors.push({ field: 'ed_masturbation_imagination', message: 'This field is required' });
      }
    }
  }

  // PE Branch validations (if main_issue is 'pe' or 'both')
  if (formData.main_issue === 'pe' || formData.main_issue === 'both') {
    if (!formData.pe_duration) {
      errors.push({ field: 'pe_duration', message: 'This field is required' });
    }
    if (!formData.pe_partner_ejaculation_time) {
      errors.push({ field: 'pe_partner_ejaculation_time', message: 'This field is required' });
    }
    if (!formData.pe_partner_penile_sensitivity) {
      errors.push({ field: 'pe_partner_penile_sensitivity', message: 'This field is required' });
    }
    if (!formData.pe_partner_masturbation_control) {
      errors.push({ field: 'pe_partner_masturbation_control', message: 'This field is required' });
    }
  }

  // Issues all partners (required if multiple_partners is "yes" AND main_issue is NOT "others") - Section 2
  if (formData.multiple_partners === 'yes' && formData.main_issue !== 'others') {
    if (!formData.issues_all_partners) {
      errors.push({ field: 'issues_all_partners', message: 'This field is required' });
    }
  }

  // Section 3: Masturbation & Behavioral History
  if (!formData.masturbation_method) {
    errors.push({ field: 'masturbation_method', message: 'This field is required' });
  }

  // Masturbation grip (required if method is 'hands' or 'both')
  if (formData.masturbation_method === 'hands' || formData.masturbation_method === 'both') {
    if (!formData.masturbation_grip) {
      errors.push({ field: 'masturbation_grip', message: 'This field is required' });
    }
  }

  // Masturbation frequency (required if method is not 'none')
  if (formData.masturbation_method !== 'none') {
    if (!formData.masturbation_frequency) {
      errors.push({ field: 'masturbation_frequency', message: 'This field is required' });
    }
  }

  if (!formData.porn_frequency) {
    errors.push({ field: 'porn_frequency', message: 'This field is required' });
  }

  if (!formData.performance_anxiety) {
    errors.push({ field: 'performance_anxiety', message: 'This field is required' });
  }

  // Multiple partners (required if current_sexual_activity is 'yes')
  if (formData.current_sexual_activity === 'yes') {
    if (!formData.multiple_partners) {
      errors.push({ field: 'multiple_partners', message: 'This field is required' });
    }
    // Partner response (required if current_sexual_activity is 'yes')
    if (!formData.partner_response) {
      errors.push({ field: 'partner_response', message: 'This field is required' });
    }
  }

  // Issues all partners (required if multiple_partners is "yes" AND main_issue is NOT "others") - validated in Section 2
  if (formData.multiple_partners === 'yes' && formData.main_issue !== 'others') {
    if (!formData.issues_all_partners) {
      errors.push({ field: 'issues_all_partners', message: 'This field is required' });
    }
  }

  // Section 4: Medical & Lifestyle
  if (!formData.medical_conditions || formData.medical_conditions.length === 0) {
    errors.push({ field: 'medical_conditions', message: 'This field is required' });
  }
  // Medical conditions other (required if 'other' is selected)
  if (formData.medical_conditions?.includes('other')) {
    if (!formData.medical_conditions_other?.trim()) {
      errors.push({ field: 'medical_conditions_other', message: 'Please specify the condition' });
    }
  }

  if (!formData.current_medications || formData.current_medications.length === 0) {
    errors.push({ field: 'current_medications', message: 'This field is required' });
  }
  // Medications other (required if 'other' is selected)
  if (formData.current_medications?.includes('other')) {
    if (!formData.current_medications_other?.trim()) {
      errors.push({ field: 'current_medications_other', message: 'Please specify the medication' });
    }
  }

  if (!formData.spinal_genital_surgery) {
    errors.push({ field: 'spinal_genital_surgery', message: 'This field is required' });
  }

  if (!formData.alcohol_frequency) {
    errors.push({ field: 'alcohol_frequency', message: 'This field is required' });
  }

  if (!formData.smoking_frequency) {
    errors.push({ field: 'smoking_frequency', message: 'This field is required' });
  }

  if (!formData.stress_level) {
    errors.push({ field: 'stress_level', message: 'This field is required' });
  }

  if (!formData.sleep_quality) {
    errors.push({ field: 'sleep_quality', message: 'This field is required' });
  }

  if (!formData.physical_activity) {
    errors.push({ field: 'physical_activity', message: 'This field is required' });
  }

  // Section 5: Other Information (optional, but validate length if provided)
  if (formData.additional_info && formData.additional_info.length > 1000) {
    errors.push({ field: 'additional_info', message: 'Additional information must be less than 1000 characters' });
  }

  return errors;
}

/**
 * Validate a single field with real-time validation support
 * Returns error message or null
 */
export function validateSingleField(
  fieldName: keyof FormData,
  value: any,
  formData: FormData,
  isRequired: boolean = false
): string | null {
  // Check required
  if (isRequired) {
    if (typeof value === 'string' && (!value || value.trim() === '')) {
      return 'This field is required';
    }
    if (Array.isArray(value) && value.length === 0) {
      return 'This field is required';
    }
    if (value === undefined || value === null || value === '') {
      return 'This field is required';
    }
  }

  // Run specific validation rule if exists
  if (validationRules[fieldName as keyof typeof validationRules]) {
    const error = validationRules[fieldName as keyof typeof validationRules](value);
    if (error && !error.startsWith('RED_FLAG')) {
      return error;
    }
  }

  // Conditional validations
  if (fieldName === 'previous_treatments' && formData.first_consultation === 'no') {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'This field is required';
    }
  }

  if (fieldName === 'medical_conditions_other' && formData.medical_conditions?.includes('other')) {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return 'Please specify the condition';
    }
  }

  if (fieldName === 'current_medications_other' && formData.current_medications?.includes('other')) {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return 'Please specify the medication';
    }
  }

  return null;
}

